'use client'

import type { ReactNode } from 'react'
import { GraduationCap } from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { schoolGradeLabel } from '@helper/utils/domain'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

export interface HomeConnectRequestDetail {
  teacherName: string
  teacherImage: string | null
  teacherRole: string | null
  schoolName: string
  schoolLogo: string | null
  studentName: string
  studentImage: string | null
  studentGrade: string | null
}

interface SummaryRowProps {
  label: string
  title: string
  caption?: string | null
  media: ReactNode
}

function SummaryRow({ label, title, caption, media }: SummaryRowProps) {
  return (
    <div className='srd:flex srd:items-center srd:gap-x-3'>
      {media}
      <span className='srd:flex srd:min-w-0 srd:flex-col'>
        <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100 srd:font-bold')}>
          {label}
        </span>
        <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black srd:truncate')}>
          {title}
        </span>
        {caption && (
          <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black')}>{caption}</span>
        )}
      </span>
    </div>
  )
}

/**
 * Who is asking, from where, about whom.
 *
 * Shared by the confirm and reject dialogs: a parent declining still needs to see exactly what they
 * are declining, and showing them two different summaries would invite the wrong answer.
 */
export function HomeConnectRequestSummary({ detail }: { detail: HomeConnectRequestDetail }) {
  const grade = detail.studentGrade ? schoolGradeLabel(detail.studentGrade) : null

  return (
    <div className='srd:flex srd:flex-col srd:gap-y-4'>
      <SummaryRow
        label='Guru'
        title={detail.teacherName}
        caption={detail.teacherRole}
        media={
          <Avatar profile='user' size='lg'>
            {detail.teacherImage && <AvatarImage src={detail.teacherImage} alt={detail.teacherName} />}
            <AvatarFallback>{getInitials(detail.teacherName)}</AvatarFallback>
          </Avatar>
        }
      />

      <SummaryRow
        label='Sekolah'
        title={detail.schoolName}
        media={
          detail.schoolLogo ? (
            <Avatar profile='organization' size='lg'>
              <AvatarImage src={detail.schoolLogo} alt={detail.schoolName} />
              <AvatarFallback>{getInitials(detail.schoolName)}</AvatarFallback>
            </Avatar>
          ) : (
            <span className='srd:bg-blue-10 srd:flex srd:size-10 srd:shrink-0 srd:items-center srd:justify-center srd:rounded-lg'>
              <GraduationCap className='srd:text-blue-100 srd:size-5' />
            </span>
          )
        }
      />

      <SummaryRow
        label='Murid'
        title={detail.studentName}
        caption={grade}
        media={
          <Avatar profile='user' size='lg'>
            {detail.studentImage && <AvatarImage src={detail.studentImage} alt={detail.studentName} />}
            <AvatarFallback>{getInitials(detail.studentName)}</AvatarFallback>
          </Avatar>
        }
      />
    </div>
  )
}
